"use client";

import { RiCloseLine } from "@remixicon/react";
import Image from "next/image";
import {
	Carousel,
	CarouselContent,
	CarouselItem,
	CarouselNext,
	CarouselPrevious,
} from "@workspace/ui/components/carousel";
import {
	Dialog,
	DialogClose,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from "@workspace/ui/components/dialog";

interface FestivalMapsModalProps {
	isOpen: boolean;
	onClose: () => void;
	mapUrls: string[];
	festivalName: string;
}

/**
 * Modal component to display festival maps in a carousel.
 */
export function FestivalMapsModal({
	isOpen,
	onClose,
	mapUrls,
	festivalName,
}: FestivalMapsModalProps) {
	if (!mapUrls || mapUrls.length === 0) {
		return null;
	}

	const hasMultipleMaps = mapUrls.length > 1;

	return (
		<Dialog open={isOpen} onOpenChange={onClose}>
			<DialogContent className="!max-w-4xl max-h-[95vh] !p-4 !gap-0 overflow-hidden" showCloseButton={false}>
				{/* Hidden title for accessibility */}
				<DialogHeader className="sr-only">
					<DialogTitle>{festivalName} Maps</DialogTitle>
				</DialogHeader>

				{/* Custom close button with circular background */}
				<DialogClose className="absolute right-6 top-6 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-black/70 text-white backdrop-blur-sm transition-colors hover:bg-black/90">
					<RiCloseLine className="h-5 w-5" />
					<span className="sr-only">Close</span>
				</DialogClose>

				<Carousel
					className="w-full"
					opts={{
						align: "start",
						loop: hasMultipleMaps,
					}}
				>
					<CarouselContent>
						{mapUrls.map((url, index) => (
							<CarouselItem key={url}>
								<div className="overflow-hidden rounded-2xl">
									<Image
										src={url}
										alt={`${festivalName} Map ${index + 1}`}
										width={1350}
										height={1687}
										className="w-full h-auto max-h-[85vh] object-cover"
										sizes="(max-width: 896px) 100vw, 896px"
										priority={index === 0}
									/>
								</div>
							</CarouselItem>
						))}
					</CarouselContent>

					{hasMultipleMaps && (
						<>
							<CarouselPrevious className="left-2 h-10 w-10" />
							<CarouselNext className="right-2 h-10 w-10" />
						</>
					)}
				</Carousel>
			</DialogContent>
		</Dialog>
	);
}
